import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Pencil, Check, X, Calculator } from 'lucide-react';

/* ─── helpers ─── */
const fmtBRL = (v) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtCoef  = (v) => Number(v).toFixed(6);
const fmtJuros = (v) =>
  ((Number(v) - 1) * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + '%';

/* ─── estilos ─── */
const VBORDER = '1px solid #1a2035';

const S = {
  page:    { maxWidth: 900, margin: '0 auto' },
  heading: { fontSize: 22, fontWeight: 700, color: '#f6c90e', marginBottom: 4 },
  sub:     { fontSize: 13, color: '#475569', marginBottom: 24 },

  inputCard: {
    background: '#0e1420',
    border: '1px solid #1a2035',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 16,
    flexWrap: 'wrap',
  },
  label: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 },
  input: {
    background: '#080c14',
    border: '1px solid #1a2035',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f6c90e',
    fontSize: 18,
    fontWeight: 700,
    outline: 'none',
    width: 220,
  },
  calcBtn: {
    background: '#f6c90e',
    color: '#080c14',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  tableCard: {
    background: '#0e1420',
    border: '1px solid #1a2035',
    borderRadius: 12,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 400,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '11px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '1px solid #1a2035',
    borderRight: VBORDER,
    textAlign: 'left',
  },
  thRight: {
    padding: '11px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '1px solid #1a2035',
    textAlign: 'right',
  },
  td: {
    padding: '11px 16px',
    fontSize: 13,
    color: '#cbd5e1',
    borderBottom: '1px solid #0f172a',
    borderRight: VBORDER,
  },
  tdRight: {
    padding: '11px 16px',
    fontSize: 13,
    color: '#cbd5e1',
    borderBottom: '1px solid #0f172a',
    textAlign: 'right',
  },
  tdBold: {
    padding: '11px 16px',
    fontSize: 13,
    fontWeight: 700,
    color: '#e2e8f0',
    borderBottom: '1px solid #0f172a',
    borderRight: VBORDER,
  },
  parcela: {
    fontSize: 15,
    fontWeight: 800,
    color: '#f6c90e',
  },
  yellow: { color: '#f6c90e', fontWeight: 700 },
  green:  { color: '#4ade80', fontWeight: 700 },

  editInput: {
    background: '#080c14',
    border: '1px solid #f6c90e',
    borderRadius: 6,
    padding: '4px 8px',
    color: '#f6c90e',
    fontSize: 13,
    width: 100,
    outline: 'none',
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 5,
    display: 'inline-flex',
    alignItems: 'center',
  },
  toast: {
    position: 'fixed', bottom: 24, right: 24,
    background: '#4ade80', color: '#052e16',
    padding: '10px 20px', borderRadius: 10,
    fontWeight: 700, fontSize: 13, zIndex: 9999,
  },
  note: {
    fontSize: 11, color: '#475569',
    borderTop: '1px solid #1a2035', padding: '10px 16px',
  },
};

export default function SimuladorParcelas() {
  const { isChefe, isGerenteComercial } = useAuth();
  const podeEditar = isChefe() || isGerenteComercial();

  const [coeficientes, setCoeficientes] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [valorStr, setValorStr]         = useState('');
  const [valor, setValor]               = useState(null);
  const [editingId, setEditingId]       = useState(null);
  const [editVal, setEditVal]           = useState('');
  const [saving, setSaving]             = useState(false);
  const [toast, setToast]               = useState('');

  const loadCoef = useCallback(() => {
    api.get('/coeficientes-parcela/')
      .then(r => setCoeficientes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCoef(); }, [loadCoef]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleCalc = () => {
    const n = parseFloat(valorStr.replace(/\./g, '').replace(',', '.'));
    if (!n || n <= 0) return;
    setValor(n);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditVal(fmtCoef(item.coeficiente));
  };

  const cancelEdit = () => { setEditingId(null); setEditVal(''); };

  const saveEdit = async (item) => {
    const n = parseFloat(editVal.replace(',', '.'));
    if (isNaN(n) || n <= 0) { cancelEdit(); return; }
    setSaving(true);
    try {
      await api.patch(`/coeficientes-parcela/${item.id}/`, { coeficiente: n.toFixed(6) });
      showToast(`Coeficiente ${item.parcela}x atualizado!`);
      loadCoef();
      cancelEdit();
    } catch {
      showToast('Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const calcRow = (item) => {
    if (!valor) return { parcelaCom: null, totalCom: null };
    const coef = parseFloat(item.coeficiente);
    const totalCom   = valor * coef;
    const parcelaCom = totalCom / item.parcela;
    return { parcelaCom, totalCom };
  };

  const handleValorChange = (e) => {
    const raw = e.target.value.replace(/[^0-9,]/g, '');
    setValorStr(raw);
  };

  const handleValorKeyDown = (e) => {
    if (e.key === 'Enter') handleCalc();
  };

  if (loading) return <div style={{ color: '#64748b', padding: 40 }}>Carregando...</div>;

  return (
    <div style={S.page}>
      {toast && <div style={S.toast}>{toast}</div>}

      {/* Cabeçalho: título + logos */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
        <div>
          <div style={S.heading}>Simulador de Parcelas</div>
          <div style={S.sub}>
            Simule o valor das parcelas com taxa.{podeEditar ? ' Clique no lapis para editar um coeficiente.' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4 }}>
          <a href="https://parceiro.bv.com.br/" target="_blank" rel="noopener noreferrer">
            <img src="/logo-banco-bv.svg" alt="Banco BV" style={{ height: 36, opacity: 0.85 }} />
          </a>
          <a href="https://acesso.77sol.com.br/" target="_blank" rel="noopener noreferrer">
            <img src="/logo-77sol-branco.svg" alt="77Sol" style={{ height: 32, opacity: 0.85 }} />
          </a>
        </div>
      </div>

      {/* input valor */}
      <div style={S.inputCard}>
        <div>
          <div style={S.label}>Valor da venda (R$)</div>
          <input
            style={S.input}
            type="text"
            placeholder="Ex: 10000,00"
            value={valorStr}
            onChange={handleValorChange}
            onKeyDown={handleValorKeyDown}
          />
        </div>
        <button style={S.calcBtn} onClick={handleCalc}>
          <Calculator size={16} />
          Calcular
        </button>
        {valor && (
          <div style={{ color: '#64748b', fontSize: 13 }}>
            Valor base:{' '}
            <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{fmtBRL(valor)}</span>
          </div>
        )}
      </div>

      {/* tabela */}
      <div style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Parcela</th>
              <th style={{ ...S.th, textAlign: 'center' }}>
                {podeEditar ? 'Coeficiente (editavel)' : 'Juros'}
              </th>
              <th style={{ ...S.th, textAlign: 'right' }}>Valor da parcela c/ taxa</th>
              <th style={S.thRight}>Valor total c/ taxa</th>
            </tr>
          </thead>
          <tbody>
            {coeficientes.map((item, idx) => {
              const { parcelaCom, totalCom } = calcRow(item);
              const rowBg    = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
              const isEditing = editingId === item.id;

              return (
                <tr key={item.id} style={{ background: rowBg }}>
                  {/* Parcela */}
                  <td style={S.tdBold}>
                    <span style={S.parcela}>{item.parcela}x</span>
                  </td>

                  {/* Coeficiente / Juros */}
                  <td style={{ ...S.td, textAlign: 'center' }}>
                    {isEditing ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input
                          style={S.editInput}
                          value={editVal}
                          onChange={e => setEditVal(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit(item);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                        />
                        <button
                          style={{ ...S.iconBtn, color: '#4ade80' }}
                          onClick={() => saveEdit(item)}
                          disabled={saving}
                          title="Salvar"
                        >
                          <Check size={15} />
                        </button>
                        <button
                          style={{ ...S.iconBtn, color: '#94a3b8' }}
                          onClick={cancelEdit}
                          title="Cancelar"
                        >
                          <X size={15} />
                        </button>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {podeEditar ? (
                          <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 13 }}>
                            {fmtCoef(item.coeficiente)}
                          </span>
                        ) : (
                          <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: 14 }}>
                            {fmtJuros(item.coeficiente)}
                          </span>
                        )}
                        {podeEditar && (
                          <button
                            style={{ ...S.iconBtn, color: '#64748b' }}
                            onClick={() => startEdit(item)}
                            title="Editar coeficiente"
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                      </span>
                    )}
                  </td>

                  {/* Valor da parcela */}
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    {parcelaCom != null ? (
                      <span style={S.yellow}>{fmtBRL(parcelaCom)}</span>
                    ) : (
                      <span style={{ color: '#334155' }}>-</span>
                    )}
                  </td>

                  {/* Valor total */}
                  <td style={S.tdRight}>
                    {totalCom != null ? (
                      <span style={S.green}>{fmtBRL(totalCom)}</span>
                    ) : (
                      <span style={{ color: '#334155' }}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={S.note}>
          * Valor da parcela com taxa = (Valor x Coeficiente) / No de parcelas &nbsp;|&nbsp;
          Valor total com taxa = Valor x Coeficiente
        </div>
      </div>
    </div>
  );
}
